import { Avatar, Box, Chip, Fab, Grid, IconButton, Paper, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { makeStyles } from 'tss-react/mui';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { get_all_followups, getDocuments, getMembersSummary, update_followup } from '../Redux/Actions/action';
import { showLoader } from '../Redux/Slices/loaderSlice';
import CommonSearchField from '../Components/Common/CommonSearchField';
import CommonTable from '../Components/Common/CommonTable';
import StyledTableCell from '../Components/Common/styledTableCell';
import ErrorHandler, { capitalizeFirstLetterOfWords, checkAccess, CommonChipFollowUpStatus, CommonStatusBox, CustomTooltip, getValuebyId, PaymentShowBox } from '../Utils/handler';
import CustomPagination from '../Components/Common/CommonPagination';
import { BucketURL } from '../ApiSetup/Api';
import DataNotFound from '../Components/Common/DataNotFound';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import CustomDialog from '../Components/Common/CustomDialog';
import ButtonContained from '../Components/Common/ButtonContained';
import CommonDropDown from '../Components/Common/CommonDropDown';
import { followUpStatusMemberRquest } from '../Utils/enum';
import TextLabel from '../Components/Common/CommonTextLabel';
import CommonTextField from '../Components/Common/CommonTextField';
import ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import CancelIcon from '@mui/icons-material/Cancel';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

dayjs.extend(relativeTime);

// ============== CONSTANTS ==============
const TAB_VALUES = {
    OTP_ONLY: "0",
    FORM_INCOMPLETE: "1",
    ACTIVE_MEMBERS: "2",
    REJECTED: "3",
    DOCS_PENDING: "4",
    NOT_INTERESTED: "5"
};

const VERIFICATION_STATUS_MAP = {
    [TAB_VALUES.OTP_ONLY]: "Login Users",
    [TAB_VALUES.FORM_INCOMPLETE]: "Draft",
    [TAB_VALUES.ACTIVE_MEMBERS]: "Approved",
    [TAB_VALUES.REJECTED]: "Rejected",
    [TAB_VALUES.DOCS_PENDING]: ["Re-Uploaded", "Partial-Rejected"],
    [TAB_VALUES.NOT_INTERESTED]: "Not-Interested"
};

const INITIAL_SORTING = {
    isPpskId: "",
    isSubmittedDate: "",
    isUpdatedDate: "",
    isRenewDays: "",
    isVerifyBy: "",
    isPaymentDate: "",
    isName: "",
    isMobile: "",
    isSubStatus: ""
};

// Sub-status configuration per tab
const SUB_STATUS_CONFIG = {
    [TAB_VALUES.OTP_ONLY]: {
        label: "Follow-up Status",
        field: "followUpStatus",
        options: [
            { label: "All", value: "All" },
            { label: "Pending", value: "Pending" },
            { label: "Called", value: "Called" },
            { label: "Not Reachable", value: "Not Reachable" },
            { label: "Call Back", value: "Call Back" },
            { label: "Wrong Number", value: "Wrong Number" },
            { label: "Not Interested", value: "Not Interested" }
        ]
    },
    [TAB_VALUES.FORM_INCOMPLETE]: {
        label: "Form Step",
        field: "userCurrentStep",
        options: [
            { label: "All", value: "All" },
            { label: "Step 1 - Personal", value: "1" },
            { label: "Step 2 - Documents", value: "2" }
        ]
    },
    [TAB_VALUES.ACTIVE_MEMBERS]: {
        label: "Payment Status",
        field: "paymentStatus",
        options: [
            { label: "All", value: "All" },
            { label: "Payment Pending", value: "Pending" },
            { label: "Payment Done", value: "Success" },
            { label: "Payment Cancelled", value: "Cancel" }
        ]
    },
    [TAB_VALUES.REJECTED]: {
        label: "Rejection Type",
        field: "rejectionType",
        options: [
            { label: "All", value: "All" }
        ]
    },
    [TAB_VALUES.DOCS_PENDING]: {
        label: "Document Status",
        field: "verificationStatus",
        options: [
            { label: "All", value: "All" },
            { label: "Re-uploaded (Waiting)", value: "Re-Uploaded" },
            { label: "Pending Re-upload", value: "Partial-Rejected" }
        ]
    },
    [TAB_VALUES.NOT_INTERESTED]: {
        label: "Last Status",
        field: "followUpStatus",
        options: [
            { label: "All", value: "All" }
        ]
    }
};

// Helper function to calculate days since a date
const getDaysSince = (date) => {
    if (!date) return null;
    return dayjs().diff(dayjs(date), 'day');
};

// Helper function to format days in a readable way
const formatDaysAgo = (days) => {
    if (days === null || days === undefined) return "-";
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
    return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`;
};

const useStyles = makeStyles()((theme) => ({
    filterChip: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
    },
    daysWarning: {
        color: theme.palette.warning.main,
        fontWeight: 600,
    },
    daysDanger: {
        color: theme.palette.error.main,
        fontWeight: 600,
    },
    daysNormal: {
        color: theme.palette.text.secondary,
    },
}))

function DocumentsViewStatus() {
    // ---------- Hook -----------
    const { classes } = useStyles();
    const theme = useTheme();
    const dispatch = useDispatch()
    const sm = useMediaQuery('(max-width:600px)');
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const dataAccess = useSelector(value => value?.allAvailableAccess?.AllAvailableAccessList);
    const availableAccess = dataAccess?.filter((e) => e.path === pathname)?.[0]?.actionsType;
    const [searchParams, setSearchParams] = useSearchParams();


    // --------------- State -------------
    const [tableData, setTableData] = useState({})
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [searchText, setSearchText] = useState("");
    const [isSearch, setIsSearch] = useState(false);
    const [candidate, setCandidate] = useState({})
    const [membersSummary, setMembersSummary] = useState();
    const [open, setOpen] = useState(false);
    const [memberId, setMemberId] = useState({});
    const [allFollowups, setAllFollowups] = useState();
    const [followUpStatus, setFollowUpStatus] = useState({
        followUpStatus: "",
        remark: ""
    });
    const [error, setError] = useState('');
    const [data, setData] = useState({});
    const [status, setStatus] = useState("All");

    // Tab configuration with clear, office-friendly names
    const tabData = useMemo(() => [
        { 
            label: "Login Users",  // Backend status name
            shortLabel: "📱 OTP Only",  // Clear: Only completed phone verification
            fullLabel: "OTP Verified Only",
            description: "Completed phone verification but haven't filled any form",
            value: TAB_VALUES.OTP_ONLY, 
            tabLabel: "Login Users Tab", 
            isView: availableAccess?.some((e) => e === "Login Users Tab"), 
            color: theme.palette.primary.main,
            bgColor: "rgba(62, 64, 148, 0.1)"
        },
        { 
            label: "Draft",
            shortLabel: "📝 Incomplete",  // Clear: Form not finished
            fullLabel: "Form Incomplete",
            description: "Started filling the form but haven't submitted yet",
            value: TAB_VALUES.FORM_INCOMPLETE, 
            tabLabel: "Draft Tab", 
            isView: availableAccess?.some((e) => e === "Draft Tab"), 
            color: "rgba(245, 134, 52, 1)",
            bgColor: "rgba(245, 134, 52, 0.1)"
        },
        { 
            label: "Approved",
            shortLabel: "✅ Active",  // Clear: Active members
            fullLabel: "Active Members",
            description: "Verified and approved members with active membership",
            value: TAB_VALUES.ACTIVE_MEMBERS, 
            tabLabel: "Approved Tab", 
            isView: availableAccess?.some((e) => e === "Approved Tab"), 
            color: "rgba(11, 176, 108, 1)",
            bgColor: "rgba(11, 176, 108, 0.1)"
        },
        { 
            label: "Rejected",
            shortLabel: "❌ Rejected",
            fullLabel: "Application Rejected",
            description: "Applications that were rejected during verification",
            value: TAB_VALUES.REJECTED, 
            tabLabel: "Rejected Tab", 
            isView: availableAccess?.some((e) => e === "Rejected Tab"), 
            color: "rgba(227, 54, 41, 1)",
            bgColor: "rgba(227, 54, 41, 0.1)"
        },
        { 
            label: "Partial-Rejected",
            shortLabel: "🔄 Re-upload",  // Clear: Need to re-upload docs
            fullLabel: "Documents Re-upload Required",
            description: "Some documents were rejected and need to be re-uploaded",
            value: TAB_VALUES.DOCS_PENDING, 
            tabLabel: "Partial-Rejected Tab", 
            isView: availableAccess?.some((e) => e === "Partial-Rejected Tab"), 
            color: "rgba(252, 171, 17, 1)",
            bgColor: "rgba(252, 171, 17, 0.1)"
        },
        { 
            label: "Not-Interested",
            shortLabel: "🚫 Not Interested",
            fullLabel: "Marked Not Interested",
            description: "Users marked as not interested in joining",
            value: TAB_VALUES.NOT_INTERESTED, 
            tabLabel: "Not Interested Tab", 
            isView: availableAccess?.some((e) => e === "Not Interested Tab"), 
            color: "#9333ea",
            bgColor: "rgba(147, 51, 234, 0.1)"
        },
    ], [availableAccess, theme.palette.primary.main]);

    const urlTab = searchParams.get("tab");
    const defaultTab = tabData.find(tab => tab.isView)?.value || "0";

    const initialTab = tabData?.some(tab => tab.value === urlTab) ? urlTab : defaultTab;

    const [tabValue, setTabValue] = useState(initialTab);
    const [value, setValue] = useState(initialTab);
    const [sorting, setSorting] = useState(INITIAL_SORTING);

    // --------------- handleChange ---------------
    const handelChangeFlolowUp = (e) => {
        const { name, value } = e.target;

        setFollowUpStatus({
            ...followUpStatus,
            [name]: value
        })
    }

    const handleClear = () => {
        setOpen(false);
        setFollowUpStatus({
            followUpStatus: "",
            remark: ""
        });
        setError("")
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const viewHandler = useCallback((item) => {
        navigate(`/admin/documents-view?id=${item._id}`);
        setCandidate(item)
    }, [navigate]);

    // Get verification status for API based on current tab
    const getVerificationStatus = useCallback((tabVal) => {
        return VERIFICATION_STATUS_MAP[tabVal] || '';
    }, []);

    // Get days class based on urgency
    const getDaysClassName = useCallback((days) => {
        if (days === null) return classes.daysNormal;
        if (days > 30) return classes.daysDanger;
        if (days > 7) return classes.daysWarning;
        return classes.daysNormal;
    }, [classes]);

    // Generate Excel with tab-specific columns
    const _generateExcel = useCallback((data) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Members');
        const currentTabData = tabData.find(t => t.value === value);
        
        // Dynamic headers based on current tab
        let headerRowValues = [
            "No",
            "PPSK ID",
            "Name",
            "Mobile No.",
            "Gender",
            "Registration Date",
            "Last Updated",
        ];

        // Add tab-specific headers
        if (value === TAB_VALUES.OTP_ONLY) {
            headerRowValues.push("Days Inactive", "Follow-up Status", "Reference Contact");
        } else if (value === TAB_VALUES.FORM_INCOMPLETE) {
            headerRowValues.push("Days Since Update", "Current Step");
        } else if (value === TAB_VALUES.ACTIVE_MEMBERS) {
            headerRowValues.push("Payment Status", "Payment Date", "Membership Start", "Membership End", "Days Remaining", "Verified By");
        } else if (value === TAB_VALUES.REJECTED) {
            headerRowValues.push("Rejected By", "Rejection Date");
        } else if (value === TAB_VALUES.DOCS_PENDING) {
            headerRowValues.push("Document Status", "Days Pending");
        } else if (value === TAB_VALUES.NOT_INTERESTED) {
            headerRowValues.push("Marked Date", "Last Remark");
        }

        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '3e4094' } };
            cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        });

        // Add data rows with tab-specific data
        data?.forEach((doc, index) => {
            let rowData = [
                index + 1,
                doc?.ppskId || "-",
                doc?.fullName || "-",
                doc?.phoneNumber || doc?.personalDetails?.phoneNumber || "-",
                doc?.personalDetails?.gender || "-",
                dayjs(doc?.createdAt).format("DD/MM/YYYY") || "-",
                dayjs(doc?.updatedAt).format("DD/MM/YYYY") || "-",
            ];

            // Add tab-specific data
            if (value === TAB_VALUES.OTP_ONLY) {
                rowData.push(
                    getDaysSince(doc?.createdAt) + " days",
                    doc?.followUpStatus || "Pending",
                    doc?.otherDetails?.referencePersonContactNumber || "-"
                );
            } else if (value === TAB_VALUES.FORM_INCOMPLETE) {
                rowData.push(
                    getDaysSince(doc?.updatedAt) + " days",
                    `Step ${(doc?.userCurrentStep || 0) + 1} of 3`
                );
            } else if (value === TAB_VALUES.ACTIVE_MEMBERS) {
                rowData.push(
                    doc?.policyStatus?.transaction?.status || "PENDING",
                    doc?.policyStatus?.transactionDate ? dayjs(doc?.policyStatus?.transactionDate).format("DD/MM/YYYY") : "-",
                    doc?.policyStatus?.activeAt ? dayjs(doc?.policyStatus?.activeAt).format("DD/MM/YYYY") : "-",
                    doc?.policyStatus?.expireAt ? dayjs(doc?.policyStatus?.expireAt).format("DD/MM/YYYY") : "-",
                    doc?.policyStatus?.remainingRenewDays ? doc?.policyStatus?.remainingRenewDays + " days" : "-",
                    doc?.documentVerificationStatus?.verifiedBy || "-"
                );
            } else if (value === TAB_VALUES.REJECTED) {
                rowData.push(
                    doc?.documentVerificationStatus?.verifiedBy || "-",
                    doc?.documentVerificationStatus?.verifiedAt ? dayjs(doc?.documentVerificationStatus?.verifiedAt).format("DD/MM/YYYY") : dayjs(doc?.updatedAt).format("DD/MM/YYYY")
                );
            } else if (value === TAB_VALUES.DOCS_PENDING) {
                rowData.push(
                    doc?.verificationStatus || "-",
                    getDaysSince(doc?.updatedAt) + " days"
                );
            } else if (value === TAB_VALUES.NOT_INTERESTED) {
                const lastFollowUp = doc?.followUp?.[doc?.followUp?.length - 1];
                rowData.push(
                    lastFollowUp?.createdAt ? dayjs(lastFollowUp?.createdAt).format("DD/MM/YYYY") : dayjs(doc?.updatedAt).format("DD/MM/YYYY"),
                    lastFollowUp?.remark || "-"
                );
            }

            worksheet.addRow(rowData);
        });

        // Auto-fit columns
        worksheet.columns.forEach(column => { column.width = 18; });
        worksheet.getColumn(1).width = 6;
        worksheet.getColumn(3).width = 30;

        // Save file
        const fileName = `${currentTabData?.fullLabel || 'Members'}_${dayjs().format('DD-MM-YYYY')}.xlsx`;
        workbook.xlsx.writeBuffer().then(buffer => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            FileSaver.saveAs(blob, fileName);
        }).catch(err => {
            console.error("Error generating Excel:", err);
            toast.error("Failed to generate Excel file");
        });
    }, [value, tabData]);

    // --------------- Api  ---------------
    const getAllFollowups = async (id) => {
        dispatch(showLoader(true))
        let body = { id: id }
        try {
            const result = await dispatch(get_all_followups(body));
            const error = ErrorHandler(result);
            if (error) {
                setAllFollowups(result?.payload?.data?.followUps)
            }
        } catch (error) {
            toast.error(error);
        } finally {
            dispatch(showLoader(false));
        }
    }

    const _addFollowups = async () => {
        if (!followUpStatus?.followUpStatus) {
            setError({ followUpStatus: "* Please select any status!" })
            return
        }
        dispatch(showLoader(true))
        try {
            let body = {
                "id": memberId,
                "currentStatus": getValuebyId(followUpStatus?.followUpStatus, followUpStatusMemberRquest),
                "remark": followUpStatus?.remark || ""
            }
            const result = await dispatch(update_followup(body))
            const error = ErrorHandler(result)
            if (error) {
                toast.success(result?.payload?.message);
                _getSummaryDetails();
                setFollowUpStatus({ followUpStatus: "", remark: "" });
                setOpen(false);
                setError({});
                _getDocumetsData();
            }
        } catch (error) {
            toast.error(error?.message || "An unknown error occurred");
        } finally {
            dispatch(showLoader(false))
        }
    }

    const _removeFromNonInterested = async (id) => {
        dispatch(showLoader(true))
        try {
            let body = {
                "id": id,
                "currentStatus": "Pending",
                "remark": ""
            }
            const result = await dispatch(update_followup(body))
            const error = ErrorHandler(result)
            if (error) {
                toast.success(result?.payload?.message);
                _getSummaryDetails();
                _getDocumetsData();
            }
        } catch (error) {
            toast.error(error?.message || "An unknown error occurred");
        } finally {
            dispatch(showLoader(false))
        }
    }

    const _getDocumetsData = useCallback(async () => {
        dispatch(showLoader(true));
        const verificationStatus = getVerificationStatus(value);
        
        const body = {
            search: searchText || '',
            page,
            limit: rowsPerPage,
            verificationStatus,
            status,
            isPpskId: sorting?.isPpskId ? +sorting?.isPpskId : "",
            isSubmittedDate: sorting?.isSubmittedDate ? +sorting?.isSubmittedDate : "",
            isUpdatedDate: sorting?.isUpdatedDate ? +sorting?.isUpdatedDate : "",
            isRenewDays: sorting?.isRenewDays ? +sorting?.isRenewDays : "",
            isVerifyBy: sorting?.isVerifyBy ? +sorting?.isVerifyBy : "",
            isPaymentDate: sorting?.isPaymentDate ? +sorting?.isPaymentDate : "",
            isName: sorting?.isName ? +sorting?.isName : "",
            isMobile: sorting?.isMobile ? +sorting?.isMobile : "",
        };

        try {
            const result = await dispatch(getDocuments(body));
            if (ErrorHandler(result)) {
                setTableData(result?.payload?.data);
            }
        } catch (error) {
            toast.error(error?.message || "Failed to fetch data");
        } finally {
            dispatch(showLoader(false));
        }
    }, [dispatch, value, searchText, page, rowsPerPage, status, sorting, getVerificationStatus]);

    const _getSummaryDetails = async () => {
        dispatch(showLoader(true))
        let body = {}
        try {
            const result = await dispatch(getMembersSummary(body));
            const error = ErrorHandler(result);
            if (error) {
                setMembersSummary(result?.payload?.data?.counts)
            }
        } catch (error) {
            toast.error(error);
        } finally {
            dispatch(showLoader(false));
        }
    }

    const _downloadMembers = useCallback(async () => {
        dispatch(showLoader(true));
        const verificationStatus = getVerificationStatus(value);
        
        const body = {
            search: searchText || '',
            verificationStatus,
            status,
            isPpskId: sorting?.isPpskId ? +sorting?.isPpskId : "",
            isSubmittedDate: sorting?.isSubmittedDate ? +sorting?.isSubmittedDate : "",
            isUpdatedDate: sorting?.isUpdatedDate ? +sorting?.isUpdatedDate : "",
            isRenewDays: sorting?.isRenewDays ? +sorting?.isRenewDays : "",
            isVerifyBy: sorting?.isVerifyBy ? +sorting?.isVerifyBy : "",
            isPaymentDate: sorting?.isPaymentDate ? +sorting?.isPaymentDate : "",
        };
        
        try {
            const result = await dispatch(getDocuments(body));
            if (ErrorHandler(result)) {
                _generateExcel(result?.payload?.data?.members);
            }
        } catch (error) {
            toast.error(error?.message || "Failed to download");
        } finally {
            dispatch(showLoader(false));
        }
    }, [dispatch, value, searchText, status, sorting, getVerificationStatus, _generateExcel]);

    // --------- useEffect ----------
    useEffect(() => {
        _getSummaryDetails()
    }, [value])

    useEffect(() => {
        if (searchText) {
            const timer = setTimeout(() => {
                _getDocumetsData()
            }, 500);
            return () => clearTimeout(timer);
        } else {
            _getDocumetsData()
        }
    }, [page, rowsPerPage, value, searchText, status, sorting])

    useEffect(() => {
        setSearchParams({ tab: tabValue }); // Store tab number in URL
    }, [tabValue, setSearchParams]);

    // Get sub-status config for current tab
    const subStatusConfig = useMemo(() => SUB_STATUS_CONFIG[value] || null, [value]);

    // Dynamic table headers based on current tab - clearer column names
    const tableHeaders = useMemo(() => {
        const baseHeaders = [
            "#",
            "Photo",
            "PPSK ID",
            "Member Name",
            "Mobile",
        ];

        const tabSpecificHeaders = {
            [TAB_VALUES.OTP_ONLY]: [
                "Gender",
                "Registered On",
                "Days Inactive",
                "Sub Status",  // Follow-up status with sorting
                "Ref. Contact",
            ],
            [TAB_VALUES.FORM_INCOMPLETE]: [
                "Gender",
                "Last Active",
                "Days Since Update",
                "Sub Status",  // Current step with sorting
            ],
            [TAB_VALUES.ACTIVE_MEMBERS]: [
                "Sub Status",  // Payment status with sorting
                "Payment Date",
                "Start Date",
                "End Date",
                "Days Left",
                "Verified By",
            ],
            [TAB_VALUES.REJECTED]: [
                "Gender",
                "Submitted On",
                "Rejected On",
                "Rejected By",
            ],
            [TAB_VALUES.DOCS_PENDING]: [
                "Gender",
                "Submitted On",
                "Days Pending",
                "Sub Status",  // Doc status with sorting
            ],
            [TAB_VALUES.NOT_INTERESTED]: [
                "Gender",
                "Registered On",
                "Marked On",
                "Sub Status",  // Last follow-up status
            ],
        };

        const headers = [...baseHeaders, ...(tabSpecificHeaders[value] || [])];
        
        if (checkAccess(pathname, dataAccess, 'Action View') || checkAccess(pathname, dataAccess, 'Action Edit')) {
            headers.push("Action");
        }
        
        return headers;
    }, [value, pathname, dataAccess]);

    // Get current tab info
    const currentTab = useMemo(() => tabData.find(t => t.value === value), [tabData, value]);
    
    // Filter options based on current tab - uses SUB_STATUS_CONFIG
    const { filterOptions, showFilter, filterPlaceholder } = useMemo(() => {
        const config = SUB_STATUS_CONFIG[value];
        if (!config || config.options.length <= 1) {
            return { filterOptions: [], showFilter: false, filterPlaceholder: "" };
        }
        return {
            filterOptions: config.options,
            showFilter: true,
            filterPlaceholder: `Filter by ${config.label}`
        };
    }, [value]);

    // Handle tab change with state reset
    const handleTabChange = useCallback((tab) => {
        setValue(tab.value);
        setTabValue(tab.value);
        setPage(1);
        setStatus("All");
        setSearchText("");
        setIsSearch(false);
        setSorting(INITIAL_SORTING);
    }, []);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper elevation={0} sx={{ padding: 2, background: "#fff", borderRadius: '20px', border: `1px solid lightgrey` }}>
                    {/* Status Chips - Compact Design */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        mb: 2
                    }}>
                        {tabData.map((tab) => {
                            if (!checkAccess(pathname, dataAccess, tab?.tabLabel)) return null;
                            const count = membersSummary?.find((e) => e?.status === tab?.label)?.statusCount || 0;
                            const isActive = value === tab.value;
                            
                            return (
                                <Chip
                                    key={tab.value}
                                    label={`${tab.shortLabel} (${count})`}
                                    onClick={() => handleTabChange(tab)}
                                    sx={{
                                        height: '36px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: isActive ? tab.color : 'transparent',
                                        color: isActive ? '#fff' : tab.color,
                                        border: `2px solid ${tab.color}`,
                                        '&:hover': {
                                            backgroundColor: isActive ? tab.color : tab.bgColor,
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                />
                            );
                        })}
                    </Box>

                    {/* Toolbar: Search + Filter + Download */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
                        {/* Left: Current status info */}
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                Showing <strong style={{ color: currentTab?.color }}>{tableData?.count || 0}</strong> {currentTab?.shortLabel} members
                            </Typography>
                        </Box>

                        {/* Right: Actions */}
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            {/* Inline Filter Dropdown for Sub Status */}
                            {showFilter && (
                                <Box sx={{ minWidth: 180 }}>
                                    <CommonDropDown
                                        text=""
                                        name="status"
                                        value={status}
                                        values={filterOptions.map((val) => val?.value)}
                                        width="100%"
                                        onChange={(e) => {
                                            setStatus(e?.target?.value);
                                            setPage(1);
                                        }}
                                        notShowSelect
                                        placeholder={filterPlaceholder}
                                    />
                                </Box>
                            )}
                            
                            {/* Show active filter indicator */}
                            {status !== "All" && (
                                <Chip 
                                    label={`Filter: ${status}`}
                                    size="small"
                                    onDelete={() => setStatus("All")}
                                    sx={{ 
                                        backgroundColor: currentTab?.bgColor,
                                        color: currentTab?.color,
                                        fontWeight: 500
                                    }}
                                />
                            )}

                            {/* Search */}
                            {!isSearch ? (
                                <Tooltip title="Search by Name, Aadhaar, Mobile">
                                    <Fab sx={{
                                        width: '42px', height: '42px', boxShadow: 'none', minHeight: "30px", padding: 0, border: '1px solid rgba(34, 34, 34, 0.3)', borderRadius: '10px', backgroundColor: "transparent", color: "rgba(34, 34, 34, 0.3)",
                                    }} onClick={() => setIsSearch(true)}>
                                        <SearchIcon sx={{ fontSize: '22px' }} />
                                    </Fab>
                                </Tooltip>
                            ) : (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <CommonSearchField 
                                        placeholder={"Search.."} 
                                        tooltipList={['Name', "Aadhaar Number", "Mobile Number", "PPSK ID"]} 
                                        handleSearch={(e) => { setPage(1); setSearchText(e); }} 
                                        value={searchText}
                                    />
                                    <Tooltip title="Close Search">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => { 
                                                setIsSearch(false); 
                                                setSearchText(""); 
                                                setPage(1);
                                            }}
                                            sx={{ 
                                                border: '1px solid rgba(34, 34, 34, 0.2)', 
                                                borderRadius: '8px',
                                                p: 0.5
                                            }}
                                        >
                                            <CancelIcon fontSize="small" color="action" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}

                            {/* Download Button */}
                            <Tooltip title={`Download ${currentTab?.label} Members`}>
                                <Fab 
                                    disabled={tableData?.members?.length === 0} 
                                    sx={{
                                        width: '42px', 
                                        height: '42px', 
                                        boxShadow: 'none', 
                                        minHeight: "30px", 
                                        padding: 0, 
                                        borderRadius: '10px', 
                                        backgroundColor: currentTab?.color, 
                                        color: theme.palette.common.white, 
                                        "&:hover": {
                                            background: currentTab?.color, 
                                            opacity: 0.9
                                        }
                                    }} 
                                    onClick={() => _downloadMembers()}
                                >
                                    <FileDownloadOutlinedIcon sx={{ fontSize: '22px' }} />
                                </Fab>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Data Table */}
                    <CommonTable headerList={tableHeaders} align={'center'} minWidth={600} setSorting={setSorting} sorting={sorting} backgroundColor={currentTab?.color}>
                        {tableData?.members?.length > 0 ? tableData?.members?.map((document, index) => {
                            const image = document?.personalDetails?.image ? BucketURL + document?.personalDetails?.image : "No";
                            const daysSinceCreated = getDaysSince(document?.createdAt);
                            const daysSinceUpdated = getDaysSince(document?.updatedAt);
                            const lastFollowUp = document?.followUp?.[document?.followUp?.length - 1];
                            
                            return (
                                <TableRow key={document?._id || index}>
                                    {/* Base columns - same for all tabs */}
                                    <StyledTableCell align="center" sx={{ width: "50px" }}>
                                        {(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Box display={'flex'} justifyContent={'center'}>
                                            <Avatar src={image} slotProps={{ img: { loading: 'lazy' } }} sx={{ borderRadius: '6px' }} />
                                        </Box>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">{document?.ppskId || "-"}</StyledTableCell>
                                    <StyledTableCell align="center">{capitalizeFirstLetterOfWords(document?.fullName || "-")}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        {document?.phoneNumber || document?.personalDetails?.phoneNumber || "-"}
                                    </StyledTableCell>

                                    {/* TAB 0: OTP Only - Users who just verified phone */}
                                    {value === TAB_VALUES.OTP_ONLY && (
                                        <>
                                            <StyledTableCell align="center">
                                                {capitalizeFirstLetterOfWords(document?.personalDetails?.gender || "-")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {dayjs(document?.createdAt).format("ll")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Typography 
                                                    variant="body2" 
                                                    className={getDaysClassName(daysSinceCreated)}
                                                    sx={{ fontWeight: daysSinceCreated > 7 ? 600 : 400 }}
                                                >
                                                    {daysSinceCreated !== null ? `${daysSinceCreated} days` : "-"}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell 
                                                align="center" 
                                                onClick={() => { setOpen(true); setMemberId(document?._id); getAllFollowups(document?._id); }}
                                                sx={{ cursor: "pointer" }}
                                            >
                                                <Tooltip title="Click to update follow-up">
                                                    <Box width={"100px"} margin="auto">
                                                        <CommonChipFollowUpStatus fontSize={"13px"} height={"28px"} label={document?.followUpStatus || "Pending"} />
                                                    </Box>
                                                </Tooltip>
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {document?.otherDetails?.referencePersonContactNumber || "-"}
                                            </StyledTableCell>
                                        </>
                                    )}

                                    {/* TAB 1: Form Incomplete - Users filling form */}
                                    {value === TAB_VALUES.FORM_INCOMPLETE && (
                                        <>
                                            <StyledTableCell align="center">
                                                {capitalizeFirstLetterOfWords(document?.personalDetails?.gender || "-")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {dayjs(document?.updatedAt).format("ll")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Typography 
                                                    variant="body2" 
                                                    className={getDaysClassName(daysSinceUpdated)}
                                                >
                                                    {daysSinceUpdated !== null ? `${daysSinceUpdated} days` : "-"}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Chip 
                                                    label={`Step ${(document?.userCurrentStep || 0) + 1} of 3`}
                                                    size="small"
                                                    sx={{ 
                                                        bgcolor: 'rgba(245, 134, 52, 0.1)', 
                                                        color: 'rgba(245, 134, 52, 1)',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            </StyledTableCell>
                                        </>
                                    )}

                                    {/* TAB 2: Active Members */}
                                    {value === TAB_VALUES.ACTIVE_MEMBERS && (
                                        <>
                                            <StyledTableCell align="center">
                                                <PaymentShowBox value={document?.policyStatus?.transaction?.status || "PENDING"} />
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {document?.policyStatus?.transactionDate ? dayjs(document?.policyStatus?.transactionDate).format("ll") : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {document?.policyStatus?.activeAt ? dayjs(document?.policyStatus?.activeAt).format("ll") : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {document?.policyStatus?.expireAt ? dayjs(document?.policyStatus?.expireAt).format("ll") : "-"}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Typography 
                                                    variant="body2"
                                                    sx={{ 
                                                        color: document?.policyStatus?.remainingRenewDays < 30 ? 'error.main' : 'success.main',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {document?.policyStatus?.remainingRenewDays ? `${document?.policyStatus?.remainingRenewDays} days` : "-"}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {document?.documentVerificationStatus?.verifiedBy || "-"}
                                            </StyledTableCell>
                                        </>
                                    )}

                                    {/* TAB 3: Rejected */}
                                    {value === TAB_VALUES.REJECTED && (
                                        <>
                                            <StyledTableCell align="center">
                                                {capitalizeFirstLetterOfWords(document?.personalDetails?.gender || "-")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {dayjs(document?.createdAt).format("ll")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {document?.documentVerificationStatus?.verifiedAt 
                                                    ? dayjs(document?.documentVerificationStatus?.verifiedAt).format("ll") 
                                                    : dayjs(document?.updatedAt).format("ll")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {document?.documentVerificationStatus?.verifiedBy || "-"}
                                            </StyledTableCell>
                                        </>
                                    )}

                                    {/* TAB 4: Documents Re-upload Required */}
                                    {value === TAB_VALUES.DOCS_PENDING && (
                                        <>
                                            <StyledTableCell align="center">
                                                {capitalizeFirstLetterOfWords(document?.personalDetails?.gender || "-")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {dayjs(document?.createdAt).format("ll")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Typography 
                                                    variant="body2" 
                                                    className={getDaysClassName(daysSinceUpdated)}
                                                >
                                                    {daysSinceUpdated !== null ? `${daysSinceUpdated} days` : "-"}
                                                </Typography>
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <CommonStatusBox fontSize={"13px"} height={"28px"} label={document?.verificationStatus} />
                                            </StyledTableCell>
                                        </>
                                    )}

                                    {/* TAB 5: Not Interested */}
                                    {value === TAB_VALUES.NOT_INTERESTED && (
                                        <>
                                            <StyledTableCell align="center">
                                                {capitalizeFirstLetterOfWords(document?.personalDetails?.gender || "-")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {dayjs(document?.createdAt).format("ll")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                {lastFollowUp?.createdAt 
                                                    ? dayjs(lastFollowUp?.createdAt).format("ll") 
                                                    : dayjs(document?.updatedAt).format("ll")}
                                            </StyledTableCell>
                                            <StyledTableCell align="center">
                                                <Tooltip title={lastFollowUp?.remark || "-"}>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                                        {lastFollowUp?.remark || "-"}
                                                    </Typography>
                                                </Tooltip>
                                            </StyledTableCell>
                                        </>
                                    )}

                                    {/* Action Column */}
                                    {(checkAccess(pathname, dataAccess, 'Action View') || checkAccess(pathname, dataAccess, 'Action Edit')) && (
                                        <StyledTableCell align="center">
                                            {value === TAB_VALUES.NOT_INTERESTED ? (
                                                <CustomTooltip title="Remove from Not Interested">
                                                    <IconButton 
                                                        sx={{ padding: 0.5 }} 
                                                        onClick={() => _removeFromNonInterested(document?._id)}
                                                    >
                                                        <CancelIcon fontSize='medium' color='error' />
                                                    </IconButton>
                                                </CustomTooltip>
                                            ) : document?.verificationStatus === 'Login Users' ? (
                                                checkAccess(pathname, dataAccess, 'Action Edit') && (
                                                    <Tooltip title="Edit Member">
                                                        <IconButton 
                                                            sx={{
                                                                padding: 0.5, 
                                                                "&:hover": {
                                                                    border: "1px solid rgba(62, 64, 149, 1)",
                                                                    background: "rgba(215, 223, 255, 1)",
                                                                    borderRadius: "10px"
                                                                }
                                                            }}
                                                            onClick={() => navigate(`/admin/addMember?id=${document?._id}&tabValue=${value}&step=0&isEdit=${true}`)}
                                                        >
                                                            <DriveFileRenameOutlineOutlinedIcon fontSize='medium' color='primary' />
                                                        </IconButton>
                                                    </Tooltip>
                                                )
                                            ) : (
                                                checkAccess(pathname, dataAccess, 'Action View') && (
                                                    <Tooltip title="View Details">
                                                        <IconButton 
                                                            sx={{
                                                                padding: 0.5, 
                                                                "&:hover": {
                                                                    border: "1px solid rgba(62, 64, 149, 1)",
                                                                    background: "rgba(215, 223, 255, 1)",
                                                                    borderRadius: "10px"
                                                                }
                                                            }} 
                                                            onClick={() => viewHandler(document)}
                                                        >
                                                            <VisibilityIcon fontSize='medium' color='primary' />
                                                        </IconButton>
                                                    </Tooltip>
                                                )
                                            )}
                                        </StyledTableCell>
                                    )}
                                </TableRow>
                            )
                        }) :
                            <TableRow>
                                <StyledTableCell colSpan={tableHeaders.length}>
                                    <DataNotFound title={'No Records Found'} icon={<ErrorOutlineIcon color="primary" style={{ fontSize: "2rem" }} />} elevation={0} />
                                </StyledTableCell>
                            </TableRow>
                        }
                    </CommonTable>
                    {tableData?.count > 0 &&
                        <Grid item xs={12}>
                            <CustomPagination
                                rowsPerPageOptions={[5, 10, 25, 50, 75, 100]}
                                count={tableData?.count}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage} />

                        </Grid>}
                </Paper>
            </Grid>
            {open &&
                <CustomDialog
                    isOpen={open} handleClose={handleClear} title={'Follow Up'} padding={'10px'}
                    maxWidth={"sm"}
                    action={<ButtonContained text={'Update'} onClick={() => _addFollowups()} minWidth={'auto'} />}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            {allFollowups?.length > 0 && allFollowups?.map((data, i) => {
                                return (
                                    <>
                                        <Grid container key={data?._id} alignItems="center" my={1} >
                                            {/* Status Button */}
                                            <Grid item xs={12} textAlign={"center"} sx={{ border: `1px dashed ${theme.palette.primary.main}`, borderRadius: "5px", padding: "5px" }}>
                                                <Box display={"flex"} justifyContent={"space-between"} >

                                                    <Box display={"flex"} flexDirection={"column"} alignItems={"start"} gap={"5px"}>
                                                        <Box display={"flex"} alignItems={"center"} gap={0.5}>
                                                            <Typography color={theme.palette.primary.main} fontSize={"12px"}>Status : </Typography>
                                                            <Typography variant="body1">
                                                                <CommonChipFollowUpStatus label={data?.currentStatus} />
                                                            </Typography>
                                                        </Box>

                                                        <Box display={"flex"} alignItems={"center"} gap={0.5}>
                                                            <Typography color={theme.palette.primary.main} fontSize={"12px"}>Date :</Typography>
                                                            <Typography variant="body2"> {data?.createdAt ? dayjs(data?.createdAt).format('LL') : "-"} </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box display={"flex"} flexDirection={"column"} alignItems={"start"} gap={"5px"}>
                                                        <Box display={"flex"} alignItems={"center"} gap={0.5}>
                                                            <Typography color={theme.palette.primary.main} fontSize={"12px"}>Verify By :</Typography>
                                                            <Typography variant="body2"> {data?.changedBy} </Typography>
                                                        </Box>

                                                        <Box display={"flex"} alignItems={"center"} gap={0.5}>
                                                            <Typography color={theme.palette.primary.main} fontSize={"12px"}>Remark :</Typography>
                                                            <Tooltip title={data?.remark}>
                                                                <Box width={"120px"} textAlign={"start"} sx={{ cursor: "pointer" }}>
                                                                    <Typography variant="body2" noWrap>
                                                                        {data?.remark || "-"}
                                                                    </Typography>
                                                                </Box>
                                                            </Tooltip>
                                                        </Box>
                                                    </Box>

                                                </Box>

                                            </Grid>
                                        </Grid>
                                    </>
                                )
                            })}
                        </Grid>

                        <Grid item xs={12} >
                            <CommonDropDown
                                type="text"
                                text={'Follow Up Status'}
                                name={'followUpStatus'}
                                value={followUpStatus?.followUpStatus}
                                values={followUpStatusMemberRquest?.map((e) => e?.label) || []}
                                labelColor={theme?.palette?.primary?.main}
                                placeholder={`Select Id Proof`}
                                width={"100%"}
                                valid
                                onChange={handelChangeFlolowUp}
                            />
                            <TextLabel color="error" title={!followUpStatus?.followUpStatus ? error?.followUpStatus : ""} />
                        </Grid>
                        <Grid item xs={12} >
                            <CommonTextField
                                value={followUpStatus?.remark}
                                text="Remark"
                                type="text"
                                onChange={handelChangeFlolowUp}
                                labelSize={{ lg: "14px", md: "14px", xs: "12px" }}
                                labelColor={theme?.palette?.primary?.main}
                                name="remark"
                                rows={3}
                                multiline
                            />
                        </Grid>
                    </Grid >
                </CustomDialog>
            }
            {/* Drawer removed - using inline filter dropdown instead */}
        </Grid >
    )
}

export default DocumentsViewStatus
